<template>
  <div class="alarm-system-container">
    <!-- 连接状态提示区域 -->
    <div v-if="isReconnecting" class="connection-status">
      <el-alert
        :title="reconnectMessage"
        type="warning"
        :closable="false"
        show-icon
      />
    </div>
     
    <!-- 报警设备网格 -->
    <div class="content-wrapper">
      <div class="group-container">
        <div v-for="group in groupedPaginatedData" :key="group.groupName" class="parameter-group">
          <div class="group-title">{{ group.groupName }}</div>
          <div class="alarm-grid">
            <div 
              v-for="(alarm, index) in group.parameters" 
              :key="index" 
              class="alarm-card"
              :class="getAlarmCardClass(getAlarmStatus(alarm))"
            >
              <div class="alarm-header">
                <div class="alarm-title">
                  <h3>{{ alarm.title }}</h3>
                  <span class="alarm-code">{{ alarm.varName }}</span>
                </div>
                <div class="alarm-status" :class="getStatusClass(getAlarmStatus(alarm))">
                  {{ getStatusText(getAlarmStatus(alarm)) }}
                </div>
              </div>
              
              <!-- 描述和重置按钮在同一行 -->
               <div class="description-reset-row">
              <!-- 只有当描述存在且与标题不同时才显示 -->
              <template v-if="alarm.description && alarm.description !== alarm.title">
                <el-tooltip
                  v-if="alarm.description.length > 20"
                  effect="dark"
                  :content="alarm.description"
                  placement="top-start"
                >
                  <div class="alarm-description">
                    {{ truncateDescription(alarm.description) }}
                  </div>
                </el-tooltip>
                <div v-else class="alarm-description">
                  {{ alarm.description }}
                </div>
              </template>
              
              <!-- 如果没有描述或描述与标题相同，添加一个占位元素保持布局 -->
              <div v-else class="alarm-description placeholder"></div>
              
              <el-button 
                v-if="alarm.varCmdSwitch === 1"
                type="primary" 
                size="small" 
                @click="handleReset(alarm)"
                :disabled="getAlarmStatus(alarm) === 'normal'"
                class="reset-btn"
              >                   
                {{ t("message.ClientDeviceMonitorSystemSettingsAlarmReset.reset") }}
              </el-button>
            </div>
              <!-- <div class="description-reset-row">
                <el-tooltip
                  v-if="alarm.description && alarm.description.length > 20"
                  effect="dark"
                  :content="alarm.description"
                  placement="top-start"
                >
                  <div class="alarm-description">
                    {{ truncateDescription(alarm.description) }}
                  </div>
                </el-tooltip>
                <div v-else class="alarm-description">
                  {{ alarm.description }}
                </div>
                
                <el-button 
                  v-if="alarm.varCmdSwitch === 1"
                  type="primary" 
                  size="small" 
                  @click="handleReset(alarm)"
                  :disabled="getAlarmStatus(alarm) === 'normal'"
                  class="reset-btn"
                >                   
                  {{ t("message.ClientDeviceMonitorSystemSettingsAlarmReset.reset") }}
                </el-button>
              </div> -->

            </div>
          </div>
        </div>
      </div>
 
      <!-- 分页区域 -->
      <div class="pagination-container">
        <div class="pagination-actions">
          <div class="selector-wrapper">
            <ClientProjectVariableSelector           
              v-model="selectedVariables"    
              :projectId="currentProjectId"
              :collectionId="currentCollectionId"
              :domainMenuId="currentDomainMenuId"
              :domainId="currentDomainId" 
              @change="handleVariableChange"
              class="variable-selector"
            />
          </div>
          <el-button 
            type="primary" 
            :icon="Refresh" 
            @click="handleQueryDataClick"
            :loading="isQeuryLoading"            
            class="refresh-btn"
          >
            {{ t("message.ClientProjectMonitorSystemMonitorParmater.queryData") }}
          </el-button>

          <el-button 
            type="primary" 
            :icon="Refresh" 
            @click="rollCallClick"
            :loading="isRollCallLoading"
            :disabled="!isConnected"
            class="refresh-btn"
          >
            {{ t("message.ClientProjectMonitorSystemMonitorParmater.refreshData") }}
          </el-button>
        </div>
        <div class="pagination">
          <el-pagination
            v-model:current-page="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="queryParams.totalCount"
            :page-sizes="[5, 10, 15, 20, 30]"
            :layout="'total, sizes, prev, pager, next, jumper'"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, defineExpose, defineProps, watch, onMounted, onUnmounted } from 'vue' 
import { ElMessage, ElMessageBox, ElTooltip } from 'element-plus'
import { Refresh } from '@element-plus/icons-vue'
import { projectCollectionsDomainVars } from "@/api/ProjectManger";
import { useI18n } from "vue-i18n";
//import baseConfig from "@/public/config.json";
import { useSignalR } from "@/utils/signalR";
import { getToken } from "@/utils/auth";
import { heart } from "@/api/UserManager";
import { rollCall } from "@/api/DeviceManagement";
import ClientProjectVariableSelector from "@/components/views/ClientProjectVariableSelector.vue";
import { getConfig } from '@/utils/configTool';

const { t } = useI18n();

// SignalR 配置
//const url = `${baseConfig.apiBaseUrl}/subscriptionHub`;
const url = ref('');
const isRollCallLoading = ref(false); // 点名刷新加载状态
const isQeuryLoading = ref(false);    // 查询加载状态
let timer = null;
const refreshInterval = 30 * 1000; // 30秒刷新一次数据
const refreshHeartInterval = 10 * 60 * 1000; // 十分钟刷新一次数据
let heartTimer = null;

// 存储键名
const getStorageKey = () => {
  return `selectedVariables_${props.projectId}_${props.collectionId}_${props.domainMenuId}_${props.domainId}`;
};

// 修改：selectedVariables 现在存储对象数组，从本地存储初始化
const selectedVariables = ref([]);

const {
  isConnected,  
  isReconnecting,
  reconnectAttempts,
  reconnectDelay,
  start,
  stop,
  invoke,
  on,
  off,
  onConnected,
} = useSignalR(url, {
  accessTokenFactory: () => getToken() || "",
  automaticReconnect: true,
});

// 重连消息
const reconnectMessage = computed(() => {
  return t("message.reaTimePlot.reconnecting", {
    attempts: reconnectAttempts.value,
    seconds: Math.round(reconnectDelay.value / 1000),
  });
});

// 定义 props
const props = defineProps({
  projectId: {
    type: [String, Number],
    required: true
  },
  collectionId: {
    type: [String, Number],
    required: true
  },
  domainMenuId: {
    type: [String, Number],
    required: true
  },
  domainId: {
    type: [String, Number],
    required: true
  }
})

// 使用 props 数据
const currentProjectId = ref(props.projectId);
const currentCollectionId = ref(props.collectionId);
const currentDomainMenuId = ref(props.domainMenuId); 
const currentDomainId = ref(props.domainId); 

// 查询参数：分页相关配置
const queryParams = reactive({
  pageNum: 1,
  pageSize: 20,
  totalCount: 0
});

// 数据相关
const parameterData = ref({
  data: [
    {
      groupName: "",
      parameters: [
        { title: "", value: 0, setValue: 0 }
      ]
    }
  ],
  totalCount: 1
});

// 加载状态控制
const isLoading = ref(false)
const hasLoaded = ref(false)
const lastLoadParams = ref(null)

// 实时数据相关
const realTimeData = ref(new Map()); // 存储变量名到最新值的映射
const isSubscribed = ref(false);



// 3. 异步加载配置函数
async function loadConfig() {
  try {
      // 获取完整配置对象
    const config = await getConfig();
    // 解构使用
    // 4. 赋值 SignalR url
    url.value = config.signalRUrl;
    //console.log('SignalR 地址配置成功：', signalRUrl.value);
  } catch (error) {
    console.error('加载配置文件失败：', error); 
  }
}

// 根据变量值判断报警状态
const getAlarmStatus = (param) => {
  if (param.value===0) return 'normal';
  
  const value = Number(param.value);
  // 这里可以根据实际业务逻辑判断状态
  if (value !==0) return 'alarm';  
  return 'normal';
};

// 截断描述文本
const truncateDescription = (description) => {
  if (!description) return '';
  if (description.length <= 20) return description;
  return description.substring(0, 20) + '...';
};
 
// 保存选中的变量到本地存储
const saveSelectedVariables = () => {
  try {
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(selectedVariables.value));
    console.log('变量选择已保存到本地存储:', selectedVariables.value);
  } catch (error) {
    console.error('保存变量选择失败:', error);
  }
};

// 从本地存储加载选中的变量
const loadSelectedVariables = () => {
  try {
    const storageKey = getStorageKey();
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        selectedVariables.value = parsed;
        console.log('从本地存储加载变量选择:', selectedVariables.value);
        return true;
      }
    }
  } catch (error) {
    console.error('加载变量选择失败:', error);
  }
  return false;
};

// 清除本地存储的变量选择
const clearSavedVariables = () => {
  try {
    const storageKey = getStorageKey();
    localStorage.removeItem(storageKey);
    console.log('已清除本地存储的变量选择');
  } catch (error) {
    console.error('清除变量选择失败:', error);
  }
};

const handleVariableChange = (newSelection) => {
  try {
    selectedVariables.value = newSelection;
    // 保存到本地存储
    saveSelectedVariables();
    console.log('变量选择已更新并保存:', newSelection);
  } catch (error) {
    ElMessage.error(`${error}`);
  }
};

// 计算属性 - 合并静态数据和实时数据
const mergedPaginatedData = computed(() => {
  if (!parameterData.value?.data?.length) return [];
  
  const mergedData = parameterData.value.data;
  // 更新实时数据
  mergedData.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName && param.deviceCode) {
        // 创建唯一的键来查找实时数据
        const uniqueKey = `${param.deviceCode}_${param.varName}`;
        const realTimeValue = realTimeData.value.get(uniqueKey);
        if (realTimeValue) {
          param.value = realTimeValue.value;
          param.updateAt = realTimeValue.timestamp;          
        }
      }
    });
  });  
  return mergedData;
});

// 模拟 C# StringComparer.Ordinal 排序（按 Unicode 编码纯字节比较）
const nameInfoCompare = (nameA, nameB) => {  
  return nameA.localeCompare(nameB, 'en-US', {
    sensitivity: 'variant',
    numeric: false,
    caseFirst: 'upper',
    usage: 'sort'
  });
};

// 优化版本：保持后端排序
const paginatedData = computed(() => {
  if (!mergedPaginatedData.value?.length) return [];
  
  const allParameters = mergedPaginatedData.value.flatMap(group => 
    (group.parameters || []).map(param => {
      // 根据 value 的小数位数计算 step
      const calculateStep = (value) => {
        if (!value && value !== 0) return 0.01; // 默认值
        
        const strValue = value.toString();
        const decimalIndex = strValue.indexOf('.');
        
        if (decimalIndex === -1) {
          // 没有小数，按个位数
          return 1;
        } else {
          // 有小数，根据小数位数计算
          const decimalPlaces = strValue.length - decimalIndex - 1;
          return Math.pow(0.1, decimalPlaces);
        }
      };
      //ElMessage.error(`${param.varCmdSwitch}-${param.varName}  paginatedData`);
      return {
        groupName: group.groupName,
        groupSort: group.groupSort || 0,
        varSort: param.varSort || 0,
        title: param.title,
        value: param.value,
        unit: param.unit,
        updateAt: param.updateAt,
        deviceCode: param.deviceCode,
        description: param.description,
        varName: param.varName || '',
        // 确保数值类型正确
        setValue: param.setValue ? Number(param.setValue) : Number(param.value),
        varCmdSwitch:param.varCmdSwitch,
        varDefaultValue:param.varDefaultValue,
        linkageVars:param.linkageVars,
        min: Number(param.min) || -1000,
        max: Number(param.max) || 10000,
        // 根据 value 的小数位数动态设置 step
        step: calculateStep(param.value),
        isSetting: param.isSetting || false
      };
    })
  );

  // 排序逻辑保持不变
  const sorted = allParameters.sort((a, b) => {
    if (a.groupSort !== b.groupSort) return a.groupSort - b.groupSort;
    const groupNameCompare = nameInfoCompare(a.groupName, b.groupName);
    if (groupNameCompare !== 0) return groupNameCompare;
    if (a.varSort !== b.varSort) return a.varSort - b.varSort;
    return nameInfoCompare(a.title, b.title);
  });
  
  return sorted;
});

const groupedPaginatedData = computed(() => {
  const data = paginatedData.value;
  if (!data.length) return [];
  
  const grouped =  Object.values(
    data.reduce((groups, item) => {
      const groupName = item.groupName;      
      if (!groups[groupName]) {
        groups[groupName] = {
          groupName,
          groupSort: item.groupSort,
          parameters: []
        };
      }
      
      groups[groupName].parameters.push({
        title: item.title,
        value: item.value,
        unit: item.unit,
        updateAt: item.updateAt,
        deviceCode: item.deviceCode,
        description: item.description,
        varSort: item.varSort,
        varName: item.varName,
        varCmdSwitch:item.varCmdSwitch,
        varDefaultValue:item.varDefaultValue,
        linkageVars:item.linkageVars,
        // 新增设置相关字段
        setValue: item.setValue,
        min: item.min,
        max: item.max,
        step: item.step,
        isSetting: item.isSetting
      });
      
      return groups;
    }, {})
  ).sort((a, b) => {
              if (a.groupSort !== b.groupSort) return a.groupSort - b.groupSort;
              return (a.groupName || '').localeCompare(b.groupName || '', 'en', { sensitivity: 'base', ignorePunctuation: false });
            })
            .map(group => ({
              ...group,
              parameters: group.parameters.sort((a, b) => {
                if (a.varSort !== b.varSort) return a.varSort - b.varSort;
                return (a.title || '').localeCompare(b.title || '', 'en', { sensitivity: 'base', ignorePunctuation: false });
              })
            }));
    
  return grouped;
});

// 状态样式方法
const getStatusClass = (status) => {
  const statusClasses = {
    'normal': 'status-normal',
    'alarm': 'status-alarm',
    'locked': 'status-locked',
    'comm-error': 'status-comm-error'
  };
  return statusClasses[status] || 'status-normal';
};

const getStatusText = (status) => {
  const statusTexts = {
    'normal': t('message.ClientDeviceMonitorSystemSettingsAlarmReset.normal'),
    'alarm': t('message.ClientDeviceMonitorSystemSettingsAlarmReset.alarm'),
    'locked': t('message.ClientDeviceMonitorSystemSettingsAlarmReset.locked'),
    'comm-error': t('message.ClientDeviceMonitorSystemSettingsAlarmReset.comm-error')
  };
  return statusTexts[status] || '正常';
};

const getAlarmCardClass = (status) => {
  const cardClasses = {
    'normal': 'card-normal',
    'alarm': 'card-alarm',
    'locked': 'card-locked',
    'comm-error': 'card-comm-error'
  };
  return cardClasses[status] || 'card-normal';
};

// 重置操作
const handleReset = async (alarm) => {
  try {
    await ElMessageBox.confirm(
      t("message.ClientDeviceMonitorSystemSettingsAlarmReset.confirmRetset", {
        title: alarm.title        
      }),
      t("message.ClientDeviceMonitorSystemSettingsAlarmReset.confirmReset"),
      {
        confirmButtonText: t("message.common.confirm"),
        cancelButtonText: t("message.common.cancel"),
        type: 'warning'
      }
    );

   await handleSetValue(alarm);
    
  } catch (error) {
    if (error !== 'cancel') {
      console.error('重置操作失败:', error);    
    }
  }
};

// 以下方法保持原有逻辑...
// [保持原有的所有方法不变，包括 loadDefaultParameters, startRealTimeSubscription, getDeviceVariableMap, stopRealTimeSubscription, handleRealTimeData, getVariableNames, convertToParameterData, handleSetValue, rollCallClick, handleQueryDataClick, chunkArray, handleSizeChange, handleCurrentChange, handleTabChange, startAutoRefresh, stopAutoRefresh 等]

const loadDefaultParameters = async (varNames = null) => {
  if (isLoading.value) return;
  
  const currentParams = {
    projectId: props.projectId,
    collectionId: props.collectionId,
    domainMenuId: props.domainMenuId,
    domainId: props.domainId,
    pageNum: queryParams.pageNum,
    pageSize: queryParams.pageSize
  };
   
  try {
    isLoading.value = true;
    
    let nameVarFilters = undefined;
    if (varNames && varNames.length > 0) {
        nameVarFilters = varNames.map(item => item.name).filter(Boolean);
    }

    const params = { 
      projectId: props.projectId,      
      collectionsId: props.collectionId,
      domainParentId: props.domainMenuId,
      domainId: props.domainId, 
      companyId: 0,
      groupNames:undefined,
      names:nameVarFilters && nameVarFilters.length > 0 ? nameVarFilters : undefined,    
      name: undefined,       
      display_name: undefined,
      pageNum: queryParams.pageNum,
      pageSize: queryParams.pageSize,
    };

    console.log('加载参数数据:', params);

    const res = await projectCollectionsDomainVars(params);
    if(!res.success) {
      ElMessage.error(res?.msg);  
      return;
    }    
    
    if (!res.data.list || res.data.list.length === 0) {
      parameterData.value = { data: [], totalCount: 0 };
      //ElMessage.warning(res.msg);
      return;
    }
    
    parameterData.value = convertToParameterData(res);
      // ElMessage.error(`${ parameterData.value.data[0].parameters[0].varCmdSwitch}-${parameterData.value.data[0].parameters[0].varName}  loadDefaultParameters`);
    queryParams.totalCount = parameterData.value.totalCount;
    hasLoaded.value = true;
    lastLoadParams.value = currentParams;
    
    // 显示订阅统计信息
    const stats = getSubscriptionStats();
    console.log('订阅统计:', stats);
    
    // 数据加载完成后启动实时订阅
    await startRealTimeSubscription();
    
  } catch (error) {
    console.error('加载业务选项失败:', error);    
    ElMessage.error(error.message || t("message.ClientProjectMonitorSystemMonitorParmater.loadFail"));
  } finally {
    isLoading.value = false;
  }
}


// 获取设备统计信息（用于调试）
const getSubscriptionStats = () => {
  const deviceVariableMap = getDeviceVariableMap();
  const stats = {
    totalDevices: deviceVariableMap.size,
    totalVariables: 0,
    devices: {}
  };
  
  for (const [deviceCode, variables] of deviceVariableMap) {
    stats.devices[deviceCode] = variables.length;
    stats.totalVariables += variables.length;
  }
  
  return stats;
}

// 启动实时数据订阅
const startRealTimeSubscription = async () => {
  if (!isConnected.value) {
    console.log('SignalR 未连接，等待连接...');
    return;
  }
  
  // 按设备代码分组变量
  const deviceVariableMap = getDeviceVariableMap();
  if (deviceVariableMap.size === 0) {
    console.log('没有可订阅的变量');
    return;
  }
  
  try {
    const token = getToken();
    
    // 为每个设备创建订阅请求
    const subscriptionPromises = [];
    
    for (const [deviceCode, variableNames] of deviceVariableMap) {
      if (variableNames.length === 0) continue;
      
      const params = {
        token: token,
        deviceCode: deviceCode,
        imei: null,
        Dir: "d",
        type: 1, // 实时数据订阅
        timer: new Date().toISOString(),
        priority: 1,
        r_data: variableNames.map(name => ({ name })),
      };
      
      const jsonString = JSON.stringify(params);
      subscriptionPromises.push(
        invoke("RequestData", jsonString)
          .then(() => {
            console.log(`设备 ${deviceCode} 订阅成功，变量数量: ${variableNames.length}`);
          })
          .catch(error => {
            console.error(`设备 ${deviceCode} 订阅失败:`, error);
            throw error;
          })
      );
    }
    
    // 并行发送所有订阅请求
    await Promise.all(subscriptionPromises);
    isSubscribed.value = true;
    console.log('所有设备实时数据订阅已启动', deviceVariableMap);
    
  } catch (error) {
    console.error('启动实时订阅失败:', error);
  }
}


// 按设备代码分组变量
const getDeviceVariableMap = () => {
  const deviceMap = new Map();
  
  if (!parameterData.value?.data?.length) return deviceMap;
  
  parameterData.value.data.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName && param.deviceCode) {
        if (!deviceMap.has(param.deviceCode)) {
          deviceMap.set(param.deviceCode, []);
        }        
        // 去重
        if (!deviceMap.get(param.deviceCode).includes(param.varName)) {
          deviceMap.get(param.deviceCode).push(param.varName);
        }
      }
    });
  });
  
  return deviceMap;
}

// 停止实时数据订阅
const stopRealTimeSubscription = async () => {
  if (!isConnected.value || !isSubscribed.value) return;
  
  try {
    
    const token = getToken();
    const deviceVariableMap = getDeviceVariableMap();
    
    const releasePromises = [];
    
    for (const [deviceCode] of deviceVariableMap) {
      const params = {
        token: token,
        deviceCode: deviceCode,
        imei: null,
        Dir: "d",
        type: 1,
        priority: 1,
        timer: new Date().toISOString(),
        r_data: [], // 空数组表示取消该设备的所有订阅
      };
      
      const jsonString = JSON.stringify(params);
      
      releasePromises.push(
        invoke("ReleaseData", jsonString)
          .then(() => {
            console.log(`设备 ${deviceCode} 取消订阅成功`);
          })
          .catch(error => {
            console.error(`设备 ${deviceCode} 取消订阅失败:`, error);
          })
      );
    }
    
    // 并行发送所有取消订阅请求
    await Promise.all(releasePromises);
    isSubscribed.value = false;
    console.log('所有设备实时数据订阅已停止');
    
  } catch (error) {
    console.error('停止实时订阅失败:', error);
  }
}

// 处理实时数据消息
const handleRealTimeData = (message) => {
  try {
    const receivedData = JSON.parse(message);
    console.log('收到实时数据:', {
      deviceCode: receivedData.DeviceCode,
      varsCount: receivedData.Vars?.length || 0,
      time: receivedData.time
    });
    
    if (receivedData.Vars && Array.isArray(receivedData.Vars)) {
      let hasUpdate = false;
      
      receivedData.Vars.forEach(variable => {
        if (variable && variable.Name && variable.Error === 0) {
          // 创建唯一的键：deviceCode + variableName
          const uniqueKey = `${receivedData.DeviceCode}_${variable.Name}`;
          
          // 更新实时数据
          realTimeData.value.set(uniqueKey, {
            value: variable.Value?.toString() || '0',
            deviceCode: receivedData.DeviceCode,
            varName: variable.Name,
            timestamp: receivedData.time.replace('T',' ')
          });
          
          hasUpdate = true;
          
          console.log(`更新变量: ${uniqueKey} = ${variable.Value}`);
        }
      });
      
      // 只有有更新时才触发界面刷新
      if (hasUpdate) {
        // 强制触发响应式更新
        parameterData.value = { ...parameterData.value };
      }
    }
  } catch (error) {
    console.error('处理实时数据错误:', error);
    ElMessage.error(`${error.message}`);
  }
}
 
// 获取所有变量的名称列表
const getVariableNames = () => {
  const variableNames = new Set();
  
  if (!parameterData.value?.data?.length) return [];
  
  parameterData.value.data.forEach(group => {
    group.parameters.forEach(param => {
      if (param.varName) {
        variableNames.add(param.varName);
      }
    });
  });
  
  return Array.from(variableNames);
}
  
 // 提取 linkageVars 处理逻辑到单独函数
 const processLinkageVars = (linkages) => {
  if (!linkages || !Array.isArray(linkages)) {
    return [];
  }
  //ElMessage.error(`${linkages[0].target_device_code} convertToParameterData`);
  return linkages
    .map(linkage => ({
      targetDeviceCode: linkage.target_device_code,
      targetVarName: linkage.target_var_name || '',
      targetVarValue: linkage.target_var_value || 0,
      linkageType: linkage.type || 1,
      conditionType: linkage.condition_type || 1,
      executionOrder: linkage.execution_order || 0,
      delaySeconds: linkage.delay_seconds || 0,
      description: linkage.description || ''
    }))
    .filter(linkage => 
      linkage.targetVarName && 
      linkage.targetVarName !== '未知参数' &&
      linkage.targetVarName.trim() !== ''
    )
    .sort((a, b) => {
      // 首先按 executionOrder 排序
      if (a.executionOrder !== b.executionOrder) {
        return a.executionOrder - b.executionOrder;
      }      
    });
};

const convertToParameterData = (res) => {
  if (!res?.data?.list) {
    return {
      data: [],
      totalCount: 0
    };
  }
   
  // 调试信息 - 检查原始数据中的 var_cmd_switch
  if (res.data.list[0]?.list_parm?.[0]) {
    const firstParam = res.data.list[0].list_parm[0];
    console.log('原始数据 var_cmd_switch:', firstParam.var_cmd_switch, '类型:', typeof firstParam.var_cmd_switch);
    //ElMessage.error(`${firstParam.var_cmd_switch}-${firstParam.var_name} convertToParameterData`);
  }

  const result = {
    data: res.data.list.map(item => ({
      groupName: item.group_name || '未分组',
      groupSort: item.group_sort || 0,
      parameters: (item.list_parm || []).map(param => {
        // 调试每个参数的 var_cmd_switch
        //console.log(`参数 ${param.var_name} 的 var_cmd_switch:`, param.var_cmd_switch);
       // ElMessage.error(`参数 ${param.var_name} 的 var_cmd_switch:${param.var_cmd_switch} convertToParameterData`);
        return {
          varName: param.var_name,
          title: param.display_name,
          value: param.var_current_value?.toString() || '0',
          unit: param.var_unit?.toString() || '',
          varSort: param.var_sort || 0,
          deviceCode: param.device_code || '',
          updateAt: param.update_at.replace('T', ' ') || '',
          description: param.description,
              // 优化 linkageVars 处理
          linkageVars: processLinkageVars(param.projectInfoBindLinkages),
          // 修正：从 param 对象中获取
          varCmdSwitch: Number(param.var_cmd_switch) || 0,
          varDefaultValue: param.var_default_value,
          setValue: Number(param.var_set_value) || 0,
          min: Number(param.min_value) || -10000,
          max: Number(param.max_value) || 10000,
          step: Number(param.step_value) || 0.01,          
          isSetting: false
        };
      }).filter(param => param.title !== '未知参数')
    })).filter(group => group.parameters.length > 0),
    totalCount: res.data.totalCount || 0
  };

  // 调试转换后的数据
  if (result.data[0]?.parameters?.[0]) {
    const firstConvertedParam = result.data[0].parameters[0];
    console.log('转换后 varCmdSwitch:', firstConvertedParam.varCmdSwitch);
  //  ElMessage.error(`${firstConvertedParam.varCmdSwitch}-${firstConvertedParam.varName} loadDefaultParameters`);
    //ElMessage.error(`${firstConvertedParam.varCmdSwitch}-${firstConvertedParam.varName} loadDefaultParameters`);
  }

  return result;
};


// 设置参数值

const handleSetValue = async (param) => {
  try {
    param.isSetting = true;
     
    if (!isConnected.value) {
      ElMessage.error(t("message.ClientDeviceMonitorSystemDebugHostDebug.connectError"));
      return;
    }

    const token = getToken();
    
    // 检查是否有联动变量
    if (!param.linkageVars || param.linkageVars.length === 0) {
      ElMessage.warning(t("message.ClientDeviceMonitorSystemSettingsAlarmReset.noLinkageVars"));
      return;
    }

    // 检查设备代码是否匹配
    for (let i = 0; i < param.linkageVars.length; i++) {
      if (param.deviceCode !== param.linkageVars[i].targetDeviceCode) {
        //ElMessage.error(`${template.id} 个变量加载11`);
        //ElMessage.warning(`${param.deviceCode}--linklength:${param.linkageVars.length},link:${param.linkageVars[i].targetDeviceCode} 个变量加载11` ||  t("message.ClientDeviceMonitorSystemSettingsAlarmReset.errorDeviceCode"));
        ElMessage.warning(t("message.ClientDeviceMonitorSystemSettingsAlarmReset.errorDeviceCode"));
        return;
      }
    }

    // 将联动变量按每5个一组分块
    const chunkSize = 5;
    const chunks = [];
    for (let i = 0; i < param.linkageVars.length; i += chunkSize) {
      chunks.push(param.linkageVars.slice(i, i + chunkSize));
    }

    let successCount = 0;

    // 依次执行每个批次
    for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
      const chunk = chunks[chunkIndex];
      
      const params = [{
        token: token,
        deviceCode: param.deviceCode,
        imei: undefined,
        Dir: "Down",
        type: 4,
        timer: new Date().toISOString(),
        priority: 1,
        r_data: [],
        w_data: [],
      }];

      // 添加读取变量（源变量）
      params[0].r_data.push({ name: param.varName });
      
      // 批量添加写入变量（每批次最多5个）
      chunk.forEach(linkageVar => {
        params[0].w_data.push({ 
          name: linkageVar.targetVarName, 
          value: linkageVar.targetVarValue 
        });
      });

      await rollCall(params);
      
      successCount += chunk.length;
      console.log(`批次 ${chunkIndex + 1}/${chunks.length} 执行成功，包含 ${chunk.length} 个联动变量`);
      
      // 可选：在批次之间添加小延迟
      if (chunkIndex < chunks.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    ElMessage.success(
      t("message.ClientDeviceMonitorSystemSettingsAlarmReset.reSetSuccess", {
        title: param.title,
        count: successCount
      })
    );

    // 点名后重新订阅实时数据
    await startRealTimeSubscription();

  } catch (error) {
    if (error !== 'cancel') {
      console.error('设置参数值失败:', error);
      ElMessage.error(
        t("message.ClientDeviceMonitorSystemSettingsAlarmReset.resetFailed", {
          title: param.title,
          error: error.message
        })
      );
    }
  } finally {
    param.isSetting = false;
  }
}


// const handleSetValue = async (param) => {
//   try {
  

//     param.isSetting = true;
     
//     if (!isConnected.value) {
//       ElMessage.error(t("message.ClientDeviceMonitorSystemDebugHostDebug.connectError"));
//       return;
//     }

//     // 获取当前页面的所有变量
//     const currentVariables = getVariableNames();
//     if (currentVariables.length === 0) {
//       ElMessage.warning(t("message.ClientDeviceMonitorSystemDebugHostDebug.noVariables"));
//       return;
//     }

//     const token = getToken();
//     const deviceVariableMap = getDeviceVariableMap();
    
//     if (deviceVariableMap.size === 0) {
//       ElMessage.warning(t("message.ClientDeviceMonitorSystemDebugHostDebug.noDevices"));
//       return;
//     }
//     for(int i=0;i<param.linkageVars.length;i++)
//     {

//       if (param.deviceCode !== param.linkageVars[i].deviceCode ) {
//         ElMessage.warning(t("message.ClientDeviceMonitorSystemSettingsAlarmReset.errorDeviceCode"));
//         return;
//       } 
//         const params = [{
//           token: token,
//           deviceCode: param.linkageVars[i].deviceCode ,
//           imei: undefined,
//           Dir: "Down",
//           type: 4, // 点名类型
//           timer: new Date().toISOString(),
//           priority: 1,
//           r_data: [],
//           w_data: [],
//         }];

//         //ElMessage.error(`${ param.varDefaultValue} handleSetValue`);
              
//         params[0].r_data.push({ name: param.varName });                 
//         params[0].w_data.push({name: param.linkageVars[i].targetVarName, value: param.linkageVars[i].targetVarValue})

//         await rollCall(params)
//           .then(() => {
//             console.log(`设备 ${param.deviceCode} 点名成功，变量数量: 1`);
//             // 设置成功提示 - 中英文
//             ElMessage.success(
//               t("message.ClientDeviceMonitorSystemSettingsAlarmReset.reSetSuccess", {
//                 title: param.title
//               })
//             );
//           })
//           .catch(error => {
//             console.error(`设备 ${param.deviceCode} 点名失败:`, error);
//             throw error;
//           });

//     }
    
//     // 点名后重新订阅实时数据
//     await startRealTimeSubscription();

//   } catch (error) {
//     if (error !== 'cancel') {
//       console.error('设置参数值失败:', error);
//       // 设置失败提示 - 中英文
//       ElMessage.error(
//         t("message.ClientDeviceMonitorSystemSettingsAlarmReset.resetFailed", {
//           title: param.title,
//           error: error.message
//         })
//       );
//     }
//   } finally {
//     param.isSetting = false;
//   }
// } 



// 优化后的点名刷新方法
const rollCallClick = async () => {
  if (!isConnected.value) {
    ElMessage.error(t("message.ClientProjectMonitorSystemMonitorParmater.connectError"));
    return;
  }

  // 获取当前页面的所有变量
  const currentVariables = getVariableNames();
  if (currentVariables.length === 0) {
    ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.noVariables"));
    return;
  }

  try {
    isRollCallLoading.value = true;
    
    const token = getToken();
    const deviceVariableMap = getDeviceVariableMap();
    
    if (deviceVariableMap.size === 0) {
      ElMessage.warning(t("message.ClientProjectMonitorSystemMonitorParmater.noDevices"));
      return;
    }

    // 为每个设备发送点名请求
    const rollCallPromises = [];
    
    for (const [deviceCode, variableNames] of deviceVariableMap) {
      if (variableNames.length === 0) continue;
      
      // 将变量名按每5个分组
      const variableChunks = chunkArray(variableNames, 5);
      
      // 为每个变量包创建请求
      for (let i = 0; i < variableChunks.length; i++) {
        const chunk = variableChunks[i];
        
        const params = {
          token: token,
          deviceCode: deviceCode,
          imei: undefined,
          Dir: "Down",
          type: 4, // 点名类型
          timer: new Date().toISOString(),
          priority: 1,
          r_data: chunk.map(name => ({ name })),
          w_data: [],
        };
        
        rollCallPromises.push(
          rollCall([params])
            .then(() => {
              console.log(`设备 ${deviceCode} 第${i + 1}/${variableChunks.length}包点名成功，变量数量: ${chunk.length}`);
            })
            .catch(error => {
              console.error(`设备 ${deviceCode} 第${i + 1}包点名失败:`, error);
              throw error;
            })
        );
        
        // 可选：添加延迟避免请求过于密集
        if (i < variableChunks.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }
    
    // 并行发送所有点名请求
    await Promise.all(rollCallPromises);
    
    ElMessage.success(
      t("message.ClientProjectMonitorSystemMonitorParmater.rollCallSuccess", {
        deviceCount: deviceVariableMap.size,
        variableCount: currentVariables.length
      })
    );
    
    // 点名后重新订阅实时数据
    await startRealTimeSubscription();
    
  } catch (error) {
    if (error !== 'cancel') {
      console.error('点名操作失败:', error);
      ElMessage.error(
        error.response?.data?.message || 
        error.message || 
        t("message.ClientProjectMonitorSystemMonitorParmater.rollCallError")
      );
    }
  } finally {
    isRollCallLoading.value = false;
  }
}

// 查询数据方法
const handleQueryDataClick = async () => {
  try {
    isQeuryLoading.value = true;
    queryParams.pageNum = 1;
    await loadDefaultParameters(selectedVariables.value);
  } catch (error) {
    console.error('查询操作失败:', error);
  } finally {
    isQeuryLoading.value = false;
  }
}

// 辅助函数：将数组按指定大小分块
const chunkArray = (array, chunkSize) => {
  const chunks = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
}

// 分页事件
const handleSizeChange = async(newSize) => {
  queryParams.pageSize = newSize;
  queryParams.pageNum = 1;
  await loadDefaultParameters(selectedVariables.value);
};

const handleCurrentChange = async (newPage) => {
  queryParams.pageNum = newPage;
  await loadDefaultParameters(selectedVariables.value);
  document.querySelector('.data-grid')?.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
};

// 标签切换事件
const handleTabChange = async (name) => {
  console.log('切换到标签:', name);
  queryParams.pageNum = 1;
  await loadDefaultParameters(selectedVariables.value);
};

// 连接成功回调
const unsubscribeConnected = onConnected(async () => {
  console.log('SignalR 连接成功');
  if (hasLoaded.value) {
    await startRealTimeSubscription();
  }
});

// 监听 props 变化
watch(
  [
    () => props.projectId,
    () => props.collectionId, 
    () => props.domainMenuId,
    () => props.domainId
  ],
  async (newValues, oldValues) => {
    const [newProjectId, newCollectionId, newDomainMenuId, newDomainId] = newValues;
    const [oldProjectId, oldCollectionId, oldDomainMenuId, oldDomainId] = oldValues || [];
    
    if (newProjectId && newCollectionId && newDomainMenuId && newDomainId) {
      const isPropsChanged = 
        newProjectId !== oldProjectId || 
        newCollectionId !== oldCollectionId || 
        newDomainMenuId !== oldDomainMenuId || 
        newDomainId !== oldDomainId;
      
      if (!oldValues || isPropsChanged) {
        try {
          // 停止旧的订阅
          await stopRealTimeSubscription();
          realTimeData.value.clear();
          
          // 重新加载保存的变量选择
          loadSelectedVariables();
          
          await loadDefaultParameters(selectedVariables.value);
        } catch (error) {
          console.error('监听props变化时加载数据失败:', error);
          ElMessage.error(error);
        }
      }
    }
  },
  { immediate: true }
);

// 开始自动刷新
const startAutoRefresh = () => {
  stopAutoRefresh();
  //首次订阅
  startRealTimeSubscription();

  timer = setInterval(() => {    
      startRealTimeSubscription();    
  }, refreshInterval);
  //启动心跳
  heartTimer = setInterval(() => {
    heart();
  }, refreshHeartInterval);
};

// 停止自动刷新
const stopAutoRefresh = () => {
  if (timer) {
    clearInterval(timer);
    timer = null;
  }

  if (heartTimer) {
    clearInterval(heartTimer);
    heartTimer = null;
  }
};

// 生命周期
onMounted(async () => {
  try {
    await loadConfig();
    // 首先加载保存的变量选择
    loadSelectedVariables();
    
    await start();
    on("ReceiveMessage", handleRealTimeData);
    startAutoRefresh();
    
    // 使用保存的变量进行初始加载
    if (selectedVariables.value.length > 0) {
      await loadDefaultParameters(selectedVariables.value);
    } else {
      await loadDefaultParameters();
    }
  } catch (error) {
    console.error('SignalR 启动失败:', error);
  }
});

onUnmounted(async () => {
  try {
    stopRealTimeSubscription();
    stopAutoRefresh();
    off("ReceiveMessage", handleRealTimeData);
    unsubscribeConnected();
    stop();
  } catch (error) {
    console.error('清理失败:', error);
  }
});

// 暴露方法给父组件
defineExpose({
  handleTabChange,
  saveSelectedVariables,
  loadSelectedVariables,
  clearSavedVariables
});
</script>

<style scoped>
.alarm-system-container {  
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  width: 100%;
  height: 85vh;
  overflow: hidden;
  box-sizing: border-box;
  color: #fff;
  padding: 5px;  
}

.system-header {
  text-align: center;
  margin-bottom: 30px;
}

.system-header h1 {
  font-size: 32px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  margin: 0;
}

.content-wrapper {
  height: 100%;
  overflow: hidden;  
  display: flex;  
  flex-direction: column;
  gap: 10px;
}

.group-container {
  flex: 1;
  overflow: auto;
}

.group-title {
  font-size: 20px;
  font-weight: 600;
  margin: 20px 0 15px 0;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
  color: #fff;
}

.alarm-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.alarm-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  padding: 20px;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.alarm-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.card-normal {
  border-color: rgba(46, 204, 113, 0.5);
}

.card-alarm {
  border-color: rgba(231, 76, 60, 0.5);
  animation: pulse-alarm 2s infinite;
}

.card-locked {
  border-color: rgba(243, 156, 18, 0.5);
}

.card-comm-error {
  border-color: rgba(149, 165, 166, 0.5);
}

@keyframes pulse-alarm {
  0% {
    box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(231, 76, 60, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(231, 76, 60, 0);
  }
}

.alarm-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.alarm-title h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
}

.alarm-code {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.alarm-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-normal {
  background: #2ecc71;
  color: white;
}

.status-alarm {
  background: #e74c3c;
  color: white;
}

.status-locked {
  background: #f39c12;
  color: white;
}

.status-comm-error {
  background: #95a5a6;
  color: white;
}

/* 新增：描述和重置按钮在同一行 */
.description-reset-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.alarm-description {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

/* 重置按钮样式调整 */
.reset-btn {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  border: none;
  font-weight: 600;
  width: 80px; /* 减小宽度 */
  flex-shrink: 0; /* 防止按钮被压缩 */
}

.reset-btn:hover:not(.is-disabled) {
  background: linear-gradient(135deg, #2980b9 0%, #2471a3 100%);
  transform: translateY(-1px);
}

.reset-btn.is-disabled {
  background: rgba(255, 255, 255, 0.2);
  color: rgba(255, 255, 255, 0.5);
  cursor: not-allowed;
}

/* 分页区域样式 */
.pagination-container {
  flex-shrink: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 10px 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  gap: 20px;
}

.pagination-actions {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 32px;
}

.selector-wrapper {
  flex: 1;
  min-width: 200px;
  max-width: 300px; 
}

.variable-selector:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  line-height: 32px;
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.refresh-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

/* 确保选择器组件内部样式正确 */
:deep(.project_variable-selector-container) {
  width: 100%;
}

:deep(.selector-header) {
  height: 32px;
  min-height: 32px;
  display: flex;
  align-items: center;
}

/* 分页区域 */
.pagination {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  min-width: 0;
}

/* Element Plus 分页组件样式重写 */
:deep(.el-pagination) {
  color: #fff;
  justify-content: flex-end;
  flex-wrap: wrap;
}

:deep(.el-pagination__total) {
  color: #fff !important;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

:deep(.el-pagination__jump) {
  color: #fff !important;
  font-weight: 500;
}

:deep(.el-pagination button) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
  transition: all 0.3s ease;
}

:deep(.el-pagination button:hover) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
  transform: translateY(-1px);
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next) {
  background: rgba(255, 255, 255, 0.1);
}

:deep(.el-pagination .el-pager li) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  border-radius: 6px;
  margin: 0 4px;
  transition: all 0.3s ease;
}

:deep(.el-pagination .el-pager li:hover) {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.4);
}

:deep(.el-pagination .el-pager li.is-active) {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  border-color: #4facfe;
  color: #fff;
  font-weight: 600;
}

:deep(.el-pagination .el-select .el-input__inner) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
}

:deep(.el-pagination .el-select .el-input__suffix .el-icon) {
  color: #fff;
}

:deep(.el-pagination .el-input__inner::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

:deep(.el-pagination .jumper .el-input__inner) {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 6px;
}

.connection-status {
  margin-bottom: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .alarm-grid {
    grid-template-columns: 1fr;
  }
  
  .description-reset-row {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  
  .reset-btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .alarm-system-container {
    padding: 10px;
  }
  
  .alarm-card {
    padding: 15px;
  }
  
  .pagination-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .pagination-actions {
    flex-direction: column;
    width: 100%;
  }
  
  .selector-wrapper {
    max-width: none;
    width: 100%;
  }
}
</style>